import { useState, useMemo, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { EntityHeader } from "../entity-header";
import { ShareableEntity } from "@/lib/api/shareable-entities";
import { EntityComments } from "../interactions/entity-comments";
import { EntityReactions } from "../interactions/entity-reactions";
import { InteractionFooter } from "../interactions/interaction-footer";
import { useEntityInteractionHandlers } from "../interactions/use-entity-interaction-handlers";
import { useBatchSignedMedia } from "@/lib/hooks/use-signed-media";
import { useLightbox } from "../shared/use-lightbox";
import { Lightbox } from "../shared/lightbox";
import { ScheduledEntityActions } from "../scheduled-entity-actions";
import { DateTime } from "luxon";

interface ViewMomentViewProps {
  entity: ShareableEntity;
  menteeId: string;
  mentorId: string;
  currentIndex?: number;
  totalEntities?: number;
  isLoadingNextPage?: boolean;
  activeTab?: "new" | "all";
  onPrevious?: () => void;
  onNext?: () => void;
  onNavigateToList?: () => void;
}

export function ViewMomentView({
  entity,
  menteeId,
  mentorId,
  currentIndex = 1,
  totalEntities = 1,
  isLoadingNextPage = false,
  activeTab,
  onPrevious,
  onNext,
  onNavigateToList,
}: ViewMomentViewProps) {
  const {
    user,
    isRead,
    isFromCurrentUser,
    comments,
    reactions,
    isInteractionPending,
    handleReaction,
    handleComment,
    toggleReadStatus,
  } = useEntityInteractionHandlers({
    entity,
    menteeId,
    entityType: "moment",
    activeTab,
    onNavigateToList,
  });

  const userIsMentor = user?.id === mentorId;

  const [activePhotoIndex, setActivePhotoIndex] = useState(0);

  // Reset state when navigating to a different entity
  useEffect(() => {
    setActivePhotoIndex(0);
  }, [entity.id]);

  const rawImages = entity.moment?.images ?? [];
  const caption = entity.moment?.caption ?? "";

  // Sign all image URLs
  const imageUrls = useMemo(
    () => rawImages.map((img) => img.url),
    [rawImages],
  );
  const signedUrls = useBatchSignedMedia(imageUrls);

  // Create images array with signed URLs
  const images = useMemo(
    () =>
      rawImages.map((img, index) => ({
        ...img,
        url: signedUrls[index]?.url ?? img.url,
      })),
    [rawImages, signedUrls],
  );

  const lightbox = useLightbox(images.length);

  // Photo navigation
  const handlePrevPhoto = () => {
    setActivePhotoIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const handleNextPhoto = () => {
    setActivePhotoIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  const isScheduled = entity.status === "scheduled";

  const headerTitle = isFromCurrentUser
    ? "Moment from you"
    : `Moment from ${entity.owner.name}`;

  const momentPreviewThumbnail = images[0] ? (
    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-md">
      <img
        src={images[0].url}
        alt="Moment thumbnail"
        className="h-full w-full object-cover"
      />
    </div>
  ) : null;

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        <EntityHeader
          title={headerTitle}
          pagination={{
            current: currentIndex,
            total: totalEntities,
            isLoadingNextPage,
            onPrevious,
            onNext,
          }}
          owner={{
            name: entity.owner.name,
            avatarUrl: entity.owner.profile_picture_url ?? undefined,
          }}
          createdAt={entity.created_at}
          subtitle={
            isScheduled && entity.scheduledAt
              ? `Scheduled for ${DateTime.fromISO(entity.scheduledAt).toFormat("MMM d, h:mm a")}`
              : undefined
          }
          actions={isScheduled ? <ScheduledEntityActions entity={entity} /> : undefined}
        />

        <div className="flex-1 overflow-y-auto pt-4">
          {/* Photo Carousel - Horizontal scroll */}
          {images.length > 0 && (
            <div className="relative mb-4">
              <div
                className="flex gap-2 px-4 transition-transform duration-300 ease-out"
                style={{
                  transform: `translateX(calc(-${activePhotoIndex * 85}% - ${activePhotoIndex * 8}px + ${activePhotoIndex > 0 ? "16px" : "0px"}))`,
                }}
              >
                {images.map((image, index) => (
                  <button
                    key={image.key}
                    type="button"
                    onClick={() => {
                      if (index === activePhotoIndex) {
                        lightbox.open(index);
                      } else {
                        setActivePhotoIndex(index);
                      }
                    }}
                    className={`relative h-[500px] flex-shrink-0 overflow-hidden rounded-2xl transition-opacity ${
                      images.length === 1 ? "w-[calc(100%-32px)]" : "w-[85%]"
                    } ${index !== activePhotoIndex ? "opacity-70" : ""}`}
                  >
                    <img
                      src={image.url}
                      alt={`Moment photo ${index + 1}`}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>

              {/* Navigation arrows */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={handlePrevPhoto}
                    disabled={activePhotoIndex === 0}
                    className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleNextPhoto}
                    disabled={activePhotoIndex === images.length - 1}
                    className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg transition-colors hover:bg-gray-50 disabled:opacity-30"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Caption */}
          {caption && (
            <div className="mx-6 rounded-xl bg-[#F5F5F0] p-4">
              <p className="font-mono text-sm leading-relaxed text-gray-800">
                {caption}
              </p>
            </div>
          )}

          {/* Show reactions if any */}
          {reactions.length > 0 && (
            <EntityReactions reactions={reactions} className="mx-6 mt-4" />
          )}

          {/* Show comments if any */}
          {comments.length > 0 && (
            <div className="mx-6 mt-4">
              <EntityComments comments={comments} />
            </div>
          )}

          {/* Empty state */}
          {images.length === 0 && (
            <div className="flex flex-1 items-center justify-center text-gray-400">
              No photos to display
            </div>
          )}
        </div>

        {/* Action bar */}
        {!isScheduled && !isFromCurrentUser && userIsMentor && (
          <InteractionFooter
            ownerName={entity.owner.name}
            isRead={isRead}
            isPending={isInteractionPending}
            previewThumbnail={momentPreviewThumbnail}
            onReaction={handleReaction}
            onComment={handleComment}
            onMarkAsRead={toggleReadStatus}
          />
        )}
      </div>

      {lightbox.isOpen && (
        <Lightbox
          images={images.map((img, i) => ({ url: img.url, alt: `Moment photo ${i + 1}` }))}
          currentIndex={lightbox.currentIndex}
          onClose={lightbox.close}
          onPrev={lightbox.prev}
          onNext={lightbox.next}
          header={<span className="text-sm font-medium">{entity.owner.name}</span>}
        />
      )}
    </>
  );
}
